import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { getEditorContainerProps } from "./nodeEditorLayout";
import { Handle, Position } from "reactflow";
import { MessageSquarePlus, Pencil, Check, Variable, Brain, BookOpen, Settings2 } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

const ANSWER_MODE_OPTIONS = [
  {
    value: "gpt_only",
    label: "ChatGPT only",
    hint: "General knowledge — ignores Knowledge Base",
  },
  {
    value: "kb_preferred",
    label: "KB preferred → GPT fallback",
    hint: "Try Knowledge Base first, then ChatGPT if missing",
  },
  {
    value: "kb_plus_gpt",
    label: "KB + ChatGPT",
    hint: "Ground in KB, allow GPT reasoning / general knowledge",
  },
];

const MODE_BADGE = {
  gpt_only: "GPT",
  kb_preferred: "KB→GPT",
  kb_plus_gpt: "KB+GPT",
};

// ─── Collapsed View ───────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const prompt = data.prompt || "";
  const variableNames = data.variableNames || [];
  const answerMode = data.answerMode || "gpt_only";
  const hasKb = Boolean(data.knowledgeBaseNodeId);

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#EDE9FE", padding: "10px", minWidth: "260px", maxWidth: "320px" }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit response generator"
      >
        <Pencil size={12} className="text-purple-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-purple-100 rounded-lg">
            <MessageSquarePlus size={14} className="text-purple-600" />
          </div>
          <span className="text-[12px] font-bold text-purple-900">AI Response Generator</span>
        </div>

        {prompt ? (
          <div className="flex items-start gap-1.5 mb-1.5">
            <Brain size={10} className="text-purple-400 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-gray-600 leading-tight">
              {prompt.length > 80 ? prompt.substring(0, 80) + "..." : prompt}
            </p>
          </div>
        ) : (
          <p className="text-[10px] text-gray-400 italic mb-1.5">No prompt set</p>
        )}

        <div className="flex flex-wrap gap-1 mt-1">
          <span className="text-[9px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">
            {MODE_BADGE[answerMode] || "GPT"}
          </span>
          {hasKb && (
            <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-0.5">
              <BookOpen size={9} /> KB
            </span>
          )}
          {data.storeInVariable && (
            <span className="text-[9px] font-mono font-semibold text-[#008069] bg-[#E7FCE8] px-1.5 py-0.5 rounded">
              → {`{{${data.storeInVariable}}}`}
            </span>
          )}
        </div>

        {variableNames.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {variableNames.slice(0, 4).map((vn, i) => (
              <span
                key={i}
                className="text-[9px] font-mono font-semibold text-[#008069] bg-[#E7FCE8] px-1.5 py-0.5 rounded"
              >
                {`{{${vn}}}`}
              </span>
            ))}
            {variableNames.length > 4 && (
              <span className="text-[9px] text-gray-400">+{variableNames.length - 4}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────
const ExpandedEditor = ({
  data,
  onSave,
  onCancel,
  flowVariables,
  onCreateVariable,
  presentation = "inline",
}) => {
  const shell = getEditorContainerProps(presentation, {
    backgroundColor: "#EDE9FE",
    borderRadius: "12px",
    padding: "14px",
    minWidth: "320px",
    maxWidth: "420px",
  });
  const fs = presentation === "fullscreen";

  const [prompt, setPrompt] = useState(data.prompt || "");
  const [systemPrompt, setSystemPrompt] = useState(data.systemPrompt || "");
  const [variableNames, setVariableNames] = useState(data.variableNames || []);
  const [answerMode, setAnswerMode] = useState(data.answerMode || "gpt_only");
  const [storeInVariable, setStoreInVariable] = useState(data.storeInVariable || "");
  const [sendReply, setSendReply] = useState(data.sendReply !== undefined ? data.sendReply : true);
  const promptRef = useRef(null);

  const buildSavePayload = () => ({
    prompt,
    systemPrompt,
    variableNames,
    answerMode,
    storeInVariable,
    sendReply,
    knowledgeBaseNodeId: data.knowledgeBaseNodeId || null,
  });

  const toggleVariable = (name) => {
    if (variableNames.includes(name)) {
      setVariableNames(variableNames.filter((v) => v !== name));
    } else {
      setVariableNames([...variableNames, name]);
    }
  };

  const insertVariable = (varName) => {
    if (!varName) return;
    const placeholder = `{{${varName}}}`;
    const textarea = promptRef.current;
    if (textarea) {
      const pos = textarea.selectionStart || prompt.length;
      const before = prompt.slice(0, pos);
      const after = prompt.slice(pos);
      setPrompt(before + placeholder + after);
    } else {
      setPrompt((prev) => prev + placeholder);
    }
    if (!variableNames.includes(varName)) {
      setVariableNames((prev) => (prev.includes(varName) ? prev : [...prev, varName]));
    }
  };

  const modeMeta = ANSWER_MODE_OPTIONS.find((o) => o.value === answerMode);

  return (
    <div className={shell.className} style={shell.style} onClick={(e) => e.stopPropagation()}>
      {!shell.hideHeader && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-200 rounded-lg">
              <MessageSquarePlus size={14} className="text-purple-700" />
            </div>
            <span className="text-[13px] font-bold text-purple-900">AI Response Generator</span>
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={() => onSave(buildSavePayload())}
              className="px-3 py-1.5 text-[11px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1 shadow-sm"
            >
              <Check size={12} /> Save
            </button>
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <NodeEditorTabs nodeType="aiResponseGenerator">
        <div className={`bg-white rounded-xl shadow-sm space-y-4 ${fs ? "p-5" : "p-3 space-y-3"}`}>
          {/* KB attachment */}
          <div
            className={`rounded-lg px-3 py-2 text-[10px] ${
              data.knowledgeBaseNodeId
                ? "bg-amber-50 text-amber-800 border border-amber-100"
                : "bg-gray-50 text-gray-500 border border-gray-100"
            }`}
          >
            <div className="flex items-center gap-1.5 font-semibold mb-0.5">
              <BookOpen size={11} /> Knowledge Base
            </div>
            {data.knowledgeBaseNodeId ? (
              <p>
                Attached. Used when mode is <b>KB preferred</b> or <b>KB + ChatGPT</b>. Connect a
                Knowledge Base node into this node to attach.
              </p>
            ) : (
              <p>
                None attached. Connect a <b>Knowledge Base</b> node → this node to ground answers.
                ChatGPT-only mode works without a KB.
              </p>
            )}
          </div>

          {/* Answer mode */}
          <div>
            <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
              <Settings2 size={10} /> Answer Mode
            </label>
            <select
              value={answerMode}
              onChange={(e) => setAnswerMode(e.target.value)}
              className="w-full px-3 py-2 text-[12px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400"
            >
              {ANSWER_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {modeMeta && <p className="text-[9px] text-gray-400 mt-1">{modeMeta.hint}</p>}
          </div>

          {/* System prompt */}
          <div>
            <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
              System Role (optional)
            </label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={fs ? 3 : 2}
              className="w-full px-3 py-2 text-[12px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none placeholder:text-gray-400"
              placeholder="You are an expert in Nice Classification for trademarks..."
            />
          </div>

          {/* Task prompt */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider">
                <Brain size={10} /> Generation Prompt
              </label>
              <VariableDropdown
                value=""
                onChange={(name) => insertVariable(name)}
                flowVariables={flowVariables}
                placeholder="Insert variable"
                className="w-36"
              />
            </div>
            <textarea
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={fs ? 5 : 4}
              className="w-full px-3 py-2 text-[12px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none placeholder:text-gray-400"
              placeholder='Identify the Nice Classification class(es) for this business type: {{business_type}}. Return class number(s) and short names.'
            />
            <p className="text-[9px] text-gray-400 mt-0.5">
              Task for ChatGPT. Use {"{{variables}}"} from earlier steps (e.g. customer answer).
            </p>
          </div>

          {/* Context Variables */}
          <div>
            <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
              <Variable size={10} /> Context Variables
            </label>
            {flowVariables.length === 0 ? (
              <p className="text-[9px] text-gray-400 italic">No variables available. Create variables first.</p>
            ) : (
              <div className="max-h-32 overflow-y-auto space-y-1">
                {flowVariables.map((v) => (
                  <label
                    key={v.id || v.name}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                      variableNames.includes(v.name) ? "bg-[#E7FCE8]" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={variableNames.includes(v.name)}
                      onChange={() => toggleVariable(v.name)}
                      className="w-3 h-3 rounded border-gray-300 text-[#008069] focus:ring-[#008069]"
                    />
                    <span className="text-[10px] font-mono font-semibold text-[#008069]">{v.name}</span>
                    {v.description && (
                      <span className="text-[9px] text-gray-400 truncate ml-auto">{v.description}</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Output options */}
          <div className="grid grid-cols-1 gap-3 pt-1 border-t border-gray-100">
            <div>
              <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">
                Store Answer In Variable
              </label>
              <VariableDropdown
                value={storeInVariable}
                onChange={setStoreInVariable}
                flowVariables={flowVariables}
                onCreateVariable={onCreateVariable}
                allowCreate
                placeholder="Optional — e.g. nice_classes"
                className="w-full"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendReply}
                onChange={(e) => setSendReply(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-[11px] text-gray-700 font-medium">Send answer to customer on WhatsApp</span>
            </label>
          </div>
        </div>
      </NodeEditorTabs>

      <div className={shell.footerClass}>
        <button
          onClick={() => onSave(buildSavePayload())}
          className="flex-1 py-2 text-[12px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save changes
        </button>
        {!shell.hideHeader && (
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main AIResponseGeneratorNode ─────────────────────
const AIResponseGeneratorNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("aiResponseGenerator");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]
  );

  return (
    <div className="relative transition-all shadow-md" style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-purple-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={closeEditor}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
          presentation="fullscreen"
        />
      </FlowNodeEditorShell>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-purple-500 !border-2 !border-white !shadow-sm"
        style={{ right: -7 }}
      />
    </div>
  );
};

export default AIResponseGeneratorNode;
